import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["kind", "size", "children", "startIcon", "icon", "removable", "clickable", "onDelete", "onClick"];
import React, { useCallback, isValidElement, cloneElement, useEffect, forwardRef } from 'react';
import { Times } from '@resultadosdigitais/tangram-react-icons';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { ScopedCssBaseline, FocusStyle } from '../StyleBase';
import { sizeStyle, kindStyle } from './constant';
import { KINDS, SIZES, isFunction } from '../helper';
import { useDeprecated } from '../hooks';

const resolveKindStyle = (kind, removable) => {
  if (removable) return kindStyle.default;
  return kindStyle[kind] ? kindStyle[kind] : kindStyle.default;
};

const resolveSizeStyle = size => {
  if (!size) return sizeStyle.md;
  return sizeStyle[size] ? sizeStyle[size] : sizeStyle.md;
};

const Root = styled.div.withConfig({
  displayName: "Tag__Root",
  componentId: "sc-12g1lok-0"
})(["", " box-sizing:content-box;border-radius:var(--border-radius-rounded);padding:0 var(--size-spacing-02);width:max-content;display:inline-flex;align-items:center;cursor:default;svg:first-of-type{margin-right:var(--size-spacing-01);}", " ", " ", " ", ""], ScopedCssBaseline, _ref => {
  let {
    tagKind,
    isRemovable
  } = _ref;
  const {
    background,
    color,
    fill
  } = resolveKindStyle(tagKind, isRemovable);
  return css(["background-color:", ";color:", ";svg{fill:", ";}"], background, color, fill);
}, _ref2 => {
  let {
    size
  } = _ref2;
  const {
    height,
    font
  } = resolveSizeStyle(size);
  return css(["height:", ";font:", ";"], height, font);
}, _ref3 => {
  let {
    onClick,
    tagKind
  } = _ref3;
  if (!onClick) return;
  const {
    backgroundHover,
    colorHover,
    fillHover
  } = resolveKindStyle(tagKind);
  return css(["", " cursor:pointer;user-select:none;&:hover,&:active,&:focus-within{color:", ";background-color:", ";svg{fill:", ";}}"], FocusStyle, colorHover, backgroundHover, fillHover);
}, _ref4 => {
  let {
    isRemovable
  } = _ref4;
  return isRemovable && css(["padding-right:0;"]);
});
const Display = styled.span.withConfig({
  displayName: "Tag__Display",
  componentId: "sc-12g1lok-1"
})(["white-space:nowrap;text-overflow:ellipsis;overflow:hidden;", ""], _ref5 => {
  let {
    isRemovable
  } = _ref5;
  return isRemovable && css(["padding-right:var(--size-spacing-01);"]);
});
const CloseButton = styled.div.withConfig({
  displayName: "Tag__CloseButton",
  componentId: "sc-12g1lok-2"
})(["padding:0 var(--size-spacing-02) 0 var(--size-spacing-01);display:flex;align-items:center;cursor:pointer;", " svg:first-of-type{fill:var(--neutral-icon-low-emphasis);margin-right:0;}&:hover:not([disabled]){background-color:transparent;svg{fill:var(--neutral-icon-high-emphasis);}}&:focus-within{outline:none;border-radius:0;box-shadow:none;}"], _ref6 => {
  let {
    size
  } = _ref6;
  const {
    height
  } = resolveSizeStyle(size);
  return css(["height:", ";"], height);
});
const Tag = /*#__PURE__*/forwardRef((_ref7, ref) => {
  let {
    kind,
    size,
    children,
    startIcon,
    icon: iconProp,
    removable,
    clickable,
    onDelete,
    onClick
  } = _ref7,
      rest = _objectWithoutPropertiesLoose(_ref7, _excluded);

  const {
    iconSize
  } = resolveSizeStyle(size);
  const isClickable = clickable && isFunction(onClick);
  const isRemovable = isFunction(onDelete) || removable;
  const icon = iconProp || startIcon;
  const renderIcon = useCallback(() => {
    if (!icon) return null;
    return /*#__PURE__*/isValidElement(icon) && /*#__PURE__*/cloneElement(icon, Object.assign({}, icon.props, {
      size: iconSize
    }));
  }, [icon, iconSize]);

  const handleDelete = event => {
    event.stopPropagation();
    onDelete(event);
  };

  useEffect(() => {
    if (kind !== 'default' && typeof children === 'string' && children.split(' ').length > 2) {
      console.warn('Tangram: is not recommended to use more thant two words');
    }
  }, [kind, children]);
  useDeprecated({
    removable,
    fallback: 'Use `onDelete` property as callback to define automatically the display with delete icon button'
  });
  useDeprecated({
    startIcon,
    fallback: 'Replace by `icon` property'
  });
  return /*#__PURE__*/React.createElement(Root, _extends({
    tagKind: kind,
    size: size,
    isRemovable: isRemovable,
    onClick: isClickable && onClick,
    role: isClickable ? 'button' : undefined
  }, rest, {
    ref: ref
  }), renderIcon(), /*#__PURE__*/React.createElement(Display, {
    isRemovable: isRemovable
  }, children), isRemovable && /*#__PURE__*/React.createElement(CloseButton, {
    size: size,
    onClick: handleDelete,
    role: "button"
  }, /*#__PURE__*/React.createElement(Times, {
    size: iconSize,
    title: "Remove tag",
    titleId: "idIconCloseTag"
  })));
});
Tag.displayName = 'Tag';
Tag.kinds = {
  default: KINDS.DEFAULT,
  primary: KINDS.PRIMARY,
  success: KINDS.SUCCESS,
  danger: KINDS.DANGER,
  warning: KINDS.WARNING,
  help: KINDS.HELP
};
Tag.sizes = {
  md: SIZES.MD,
  lg: SIZES.LG,
  xl: SIZES.XL
};
Tag.defaultProps = {
  kind: Tag.kinds.default,
  clickable: true,
  size: Tag.sizes.MD
};
process.env.NODE_ENV !== "production" ? Tag.propTypes = {
  /**
   * Sets the children.
   */
  children: PropTypes.oneOfType([PropTypes.element, PropTypes.string, PropTypes.array, PropTypes.node]).isRequired,

  /**
   * Set element type to be rendered for this component. By default it will be render a `div` element.
   */
  as: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),

  /**
   * Allows interaction in the component using defined styles as hover, focus, active. When is `false` the `Tag` will not be clickable, even if `onClick` prop is defined.
   */
  clickable: PropTypes.bool,

  /**
   * Sets the kind.
   */
  kind: PropTypes.oneOf([Tag.kinds.default, Tag.kinds.primary, Tag.kinds.success, Tag.kinds.warning, Tag.kinds.danger, Tag.kinds.help]),

  /**
   * Sets the icon which will be positioned at the beginning.
   */
  icon: PropTypes.node,

  /**
   * Callback fired when the `Tag` is clicked.
   *
   * @param {React.SyntheticEvent} event - The event source of the callback.
   */
  onClick: PropTypes.func,

  /**
   * Callback fired when the close button is clicked.
   * If defined will display a delete button and keep the kind style as default `Tag.kinds.default` automatically.
   *
   * @param {React.SyntheticEvent} event - The event source of the callback.
   */
  onDelete: PropTypes.func,

  /**
   * Sets the size.
   */
  size: PropTypes.oneOf([Tag.sizes.md, Tag.sizes.lg, Tag.sizes.xl]),

  /**
   * @deprecated - Use `onDelete` property as callback to define automatically the display with delete icon button.
   *
   * Sets a type removable tag.
   */
  removable: PropTypes.bool,

  /**
   * @deprecated - Use `icon` property instead of it.
   *
   * Sets the icon on start.
   */
  startIcon: PropTypes.node
} : void 0;
export default Tag;